import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CandidateProfileModal = ({ candidate, onClose, onStatusChange, onScheduleInterview, onAddNote }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [note, setNote] = useState('');

  if (!candidate) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'resume', label: 'Resume', icon: 'FileText' },
    { id: 'notes', label: 'Notes', icon: 'MessageSquare' },
    { id: 'activity', label: 'Activity', icon: 'Activity' }
  ];

  const handleAddNote = () => {
    if (note?.trim()) {
      onAddNote(candidate?.id, note);
      setNote('');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000]" onClick={onClose} />
      <div className="fixed inset-4 md:inset-8 lg:inset-16 bg-card border border-border rounded-lg shadow-modal z-[2001] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Candidate Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 bg-muted mx-auto md:mx-0">
                <Image
                  src={candidate?.avatar}
                  alt={candidate?.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-semibold text-foreground mb-2">{candidate?.name}</h3>
                <p className="text-base text-muted-foreground mb-3">{candidate?.position}</p>
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{candidate?.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Briefcase" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{candidate?.experience}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Target" size={16} className="text-primary" />
                    <span className="text-foreground font-medium">{candidate?.skillMatch}% Match</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => onScheduleInterview(candidate?.id)}
                  >
                    Schedule Interview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Mail"
                    iconPosition="left"
                  >
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Resume
                  </Button>
                </div>
              </div>
            </div>

            <div className="border-b border-border mb-6">
              <div className="flex gap-1 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <Icon name="Mail" size={18} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm text-foreground">{candidate?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <Icon name="Phone" size={18} className="text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-sm text-foreground">{candidate?.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Professional Summary</h4>
                  <p className="text-sm text-foreground leading-relaxed">{candidate?.summary}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Key Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {candidate?.skills?.map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-md">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Salary Expectations</h4>
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <Icon name="IndianRupee" size={18} className="text-primary" />
                    <span className="text-sm text-foreground">{candidate?.expectedSalary}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'resume' && (
              <div className="space-y-4">
                <div className="p-6 bg-muted rounded-lg text-center">
                  <Icon name="FileText" size={48} className="mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-foreground mb-4">Resume preview would appear here</p>
                  <Button variant="default" iconName="Download" iconPosition="left">
                    Download Resume
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Add a note about this candidate..."
                    value={note}
                    onChange={(e) => setNote(e?.target?.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={handleAddNote}
                  >
                    Add
                  </Button>
                </div>

                <div className="space-y-3">
                  {candidate?.notes && candidate?.notes?.map((noteItem, index) => (
                    <div key={index} className="p-4 bg-muted rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-muted-foreground">{noteItem?.date}</span>
                        <span className="text-xs text-muted-foreground">{noteItem?.author}</span>
                      </div>
                      <p className="text-sm text-foreground">{noteItem?.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-3">
                {candidate?.activity && candidate?.activity?.map((item, index) => (
                  <div key={index} className="flex gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground mb-1">{item?.description}</p>
                      <p className="text-xs text-muted-foreground">{item?.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateProfileModal;